import {
  Dialog,
  DialogActions,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { Box, Divider } from "@mui/material";
import { Close } from "@material-ui/icons";
import Button from "components/Forms/CustomButtons/ButtonTS";

import { addDays, isAfter, isBefore, parse } from "date-fns";

import { useCallback, useMemo, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTranslation } from "react-i18next";
import { useKeycloak } from "@react-keycloak/web";
import { ActivityItem } from "store/activityItem/activityItemContext.types";
import { useActivityItemContext } from "store/activityItem";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import { formatDate, parseISOWithoutTimezoneOffset } from "utils/functions";
import { Checkbox, FormControlLabel, Tooltip, Typography } from "@mui/material";
import { useClientConfig } from "hooks/useClientConfig";

import { InfoOutlined } from "@mui/icons-material";

interface ExpirationDateModalProps {
  isUploading: boolean;
  modalOpen: boolean;
  onClose: () => void;
  onConfirm?: (
    validationExpirationDate?: Date,
    validationExpirationStartDate?: Date
  ) => void;
  onConfimSuperUser?: (
    activity: ActivityItem,
    validationExpirationDate: Date | string,
    reason: string | any,
    hasInfiniteLifetime?: boolean,
    validationExpirationStartDate?: Date | string
  ) => void;
  entityDate?: {
    start: string;
    end: string;
  };
  hasReason?: boolean;
  hasApprovalWithoutExpiration?: boolean;
  notPreSetDates?: boolean;
}

export default function ExpirationDateModal({
  isUploading,
  modalOpen,
  onClose,
  onConfirm,
  onConfimSuperUser,
  entityDate,
  hasReason = false,
  hasApprovalWithoutExpiration = false,
  notPreSetDates = false,
}: ExpirationDateModalProps): JSX.Element {
  const { keycloak } = useKeycloak();
  const isSuperUser = keycloak.hasRealmRole("superUser");

  const { t } = useTranslation("activity-item-table");

  const { currentActivityItem } = useActivityItemContext((state) => ({
    currentActivityItem: state.currentActivity,
  }));

  const { clientConfigSidebar } = useClientConfig();

  const [fieldExpirationDate, setFieldExpirationDate] = useState<
    Date | undefined
  >(() =>
    currentActivityItem?.expirationDateBeforeValidation && !notPreSetDates
      ? parseISOWithoutTimezoneOffset(
          currentActivityItem?.expirationDateBeforeValidation
        )
      : undefined
  );

  const [fieldStartDate, setFieldStartDate] = useState<Date | undefined>(() =>
    currentActivityItem?.validationExpirationStartDate && !notPreSetDates
      ? parseISOWithoutTimezoneOffset(
          currentActivityItem?.validationExpirationStartDate
        )
      : undefined
  );

  const [reason, setReason] = useState<string>("");

  const [checked, setChecked] = useState<boolean>(false);

  const todayAtMidnight = new Date();
  todayAtMidnight.setHours(0, 0, 0, 0);

  const minDate = addDays(todayAtMidnight, 0);
  const entityStartDate = entityDate?.start
    ? parse(entityDate.start, "dd/MM/yyyy", todayAtMidnight)
    : null;

  const entityEndDate = entityDate?.end
    ? parse(entityDate.end, "dd/MM/yyyy", todayAtMidnight)
    : null;

  const handleChangeCheckBox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const handleOnConfirm = () => {
    if (!checked && !fieldExpirationDate) return;
    const normalizedDate = new Date(fieldExpirationDate);
    normalizedDate?.setHours(0, 0, 0, 0);

    if (currentActivityItem && onConfimSuperUser) {
      onConfimSuperUser(
        currentActivityItem,
        normalizedDate,
        reason,
        checked,
        fieldStartDate
      );
    } else if (onConfirm) {
      onConfirm(normalizedDate, fieldStartDate);
    }

    setFieldExpirationDate(undefined);
    setFieldStartDate(undefined);
  };

  const renderExpirationDate = useCallback(() => {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
        <DatePicker
          className="datePicker"
          label={t("expiration-date", { ns: "manual-validation-modal" })}
          sx={{ fontSize: "1rem", textAlign: "center" }}
          onChange={(d) => {
            if (typeof d !== "string") {
              setFieldExpirationDate(d);
            }
          }}
          value={fieldExpirationDate}
          closeOnSelect
          minDate={addDays(todayAtMidnight, 0)}
          shouldDisableDate={(current) => {
            if (entityStartDate) {
              if (isAfter(todayAtMidnight, entityStartDate)) {
                return isBefore(current, minDate);
              } else if (isBefore(todayAtMidnight, entityStartDate)) {
                const adjustedMinDate = addDays(entityStartDate, 0);
                return isBefore(current, adjustedMinDate);
              }
            }

            return isBefore(current, minDate);
          }}
          disabled={checked}
        />
      </LocalizationProvider>
    );
  }, [checked, fieldExpirationDate, entityStartDate]);

  const renderInitialAndFinishedDate = useCallback(() => {
    const isStartDateInvalid =
      fieldStartDate &&
      (isBefore(fieldStartDate, entityStartDate) ||
        isAfter(fieldStartDate, entityEndDate));

    const isEndDateBeforeStartDate =
      fieldExpirationDate &&
      fieldStartDate &&
      isBefore(fieldExpirationDate, fieldStartDate);

    const isEndDateOutsideEntityRange =
      fieldExpirationDate &&
      (isBefore(fieldExpirationDate, entityStartDate) ||
        isAfter(fieldExpirationDate, entityEndDate));

    return (
      <Box display={"flex"} flexDirection="column">
        <Box
          flexDirection="row"
          display="flex"
          justifyContent={"space-between"}
        >
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ptBR}
          >
            <DatePicker
              className="datePicker"
              label={t("start-date", { ns: "manual-validation-modal" })}
              sx={{ fontSize: "1rem", textAlign: "center" }}
              onChange={(d) => {
                if (typeof d !== "string") {
                  setFieldStartDate(d);
                }
              }}
              value={fieldStartDate}
              closeOnSelect
              minDate={addDays(entityStartDate, 0)}
              maxDate={addDays(entityEndDate, -1)}
              disabled={checked}
            />
          </LocalizationProvider>
          <Divider sx={{ fontSize: "2rem" }}> </Divider>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ptBR}
          >
            <DatePicker
              className="datePicker"
              label={t("end-date", { ns: "manual-validation-modal" })}
              sx={{ fontSize: "1rem", textAlign: "center" }}
              onChange={(d) => {
                if (typeof d !== "string") {
                  setFieldExpirationDate(d);
                }
              }}
              value={fieldExpirationDate}
              closeOnSelect
              minDate={addDays(fieldStartDate || entityStartDate, 0)}
              maxDate={addDays(entityEndDate, 0)}
              disabled={checked || !fieldStartDate?.valueOf()}
            />
          </LocalizationProvider>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="start"
          width={"100%"}
        >
          {(isStartDateInvalid ||
            isEndDateOutsideEntityRange ||
            isEndDateBeforeStartDate) && (
            <p
              style={{
                color: "#be2e2e",
                fontSize: "0.8rem",
                marginTop: "0.5rem",
              }}
            >
              {isEndDateBeforeStartDate
                ? t("end-date-before-start-date")
                : `${t("info-date")}  ${formatDate(
                    entityStartDate
                  )} - ${formatDate(entityEndDate)}`}
            </p>
          )}
        </Box>
      </Box>
    );
  }, [
    fieldStartDate,
    fieldExpirationDate,
    entityStartDate,
    entityEndDate,
    checked,
  ]);

  const hasDisabledValidation = useMemo(() => {
    if (currentActivityItem?.hasStartDate) {
      const isInvalidStartDate =
        fieldStartDate &&
        (isBefore(fieldStartDate, entityStartDate) ||
          isAfter(fieldStartDate, entityEndDate));

      const isInvalidEndDate =
        fieldExpirationDate &&
        (isBefore(fieldExpirationDate, fieldStartDate) ||
          isAfter(fieldExpirationDate, entityEndDate));

      return (
        isUploading ||
        (!checked &&
          (!fieldStartDate?.valueOf() || !fieldExpirationDate?.valueOf())) ||
        isInvalidStartDate ||
        isInvalidEndDate ||
        isAfter(entityStartDate, todayAtMidnight) ||
        (isSuperUser && !reason && hasReason)
      );
    }

    const isInvalidExpirationDate =
      fieldExpirationDate &&
      (isBefore(fieldExpirationDate, todayAtMidnight) ||
        (entityStartDate && isBefore(fieldExpirationDate, entityStartDate)));

    return (
      isUploading ||
      (!checked && !fieldExpirationDate?.valueOf()) ||
      isInvalidExpirationDate ||
      (isSuperUser && !reason && hasReason)
    );
  }, [
    isSuperUser,
    isUploading,
    checked,
    todayAtMidnight,
    entityStartDate,
    entityEndDate,
    fieldExpirationDate,
    fieldStartDate,
    currentActivityItem?.hasStartDate,
    reason,
    hasReason,
  ]);

  return (
    <Dialog
      open={modalOpen}
      fullWidth
      scroll="body"
      PaperProps={{
        style: {
          overflowY: "visible",
          width: "550px",
        },
      }}
      onClose={() => {
        onClose();
      }}
      disableBackdropClick
    >
      <DialogTitle>
        {hasReason
          ? t("activity-approval", { ns: "manual-validation-modal" })
          : t("set-expiration-activity")}
        <Button
          justIcon
          simple
          color="tumblr"
          round
          size="sm"
          style={{
            position: "absolute",
            right: "10px",
            top: "10px",
          }}
          onClick={onClose}
        >
          <Close />
        </Button>
      </DialogTitle>
      <Box display="flex" flexDirection="column" padding={"0 1rem 1rem 1rem"}>
        {currentActivityItem?.hasStartDate
          ? renderInitialAndFinishedDate()
          : renderExpirationDate()}

        {clientConfigSidebar?.hasApprovalWithoutExpiration &&
          isSuperUser &&
          hasApprovalWithoutExpiration && (
            <Box display="flex" alignItems="center" justifyContent="flex-start">
              <Tooltip
                title={
                  <div>
                    <Typography variant="inherit">
                      {t("selecting-this-option", {
                        ns: "manual-validation-modal",
                      })}
                    </Typography>
                    <ul
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        paddingLeft: "1.5rem",
                        gap: "0.4rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      <li>
                        {t("activity-marked-complete", {
                          ns: "manual-validation-modal",
                        })}
                      </li>
                      <li>
                        {t("expiration-date-set", {
                          ns: "manual-validation-modal",
                        })}
                      </li>
                      <li>
                        {t("activity-not-reopened", {
                          ns: "manual-validation-modal",
                        })}
                      </li>
                    </ul>
                  </div>
                }
                placement="right-start"
                arrow={true}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={handleChangeCheckBox}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: "1.2rem", // Ajuste do tamanho do ícone
                        },
                      }}
                    />
                  }
                  label={
                    <>
                      {t("permanent-approval", {
                        ns: "manual-validation-modal",
                      })}
                      <InfoOutlined
                        sx={{ fontSize: 15, marginLeft: "0.5rem" }}
                      />
                    </>
                  }
                  sx={{
                    ".MuiFormControlLabel-label": {
                      color: "#000000de", // Use sua cor personalizada
                      fontWeight: "400",
                      fontSize: "0.9rem",
                    },
                  }}
                />
              </Tooltip>
            </Box>
          )}

        {isSuperUser && hasReason && (
          <>
            <Typography
              variant="overline"
              color="textSecondary"
              style={{ marginTop: "16px" }}
            >
              {t("validation-reason", { ns: "manual-validation-modal" })}
            </Typography>
            <TextField
              disabled={isUploading}
              variant="outlined"
              className="cy-reason-text-field"
              value={reason}
              multiline
              rows={4}
              onChange={(e) => setReason(e.target.value)}
            />
          </>
        )}
      </Box>
      <DialogActions>
        <Button disabled={isUploading} color="tumblr" simple onClick={onClose}>
          {t("cancel")}
        </Button>

        <Button
          disabled={hasDisabledValidation}
          color="primary"
          onClick={handleOnConfirm}
        >
          {t("validate")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
